import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, UsersRound, Lock } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const STATS = [
  { key: 'critical_positions', es: 'Posiciones críticas', en: 'Critical positions' },
  { key: 'with_successors', es: 'Con sucesores identificados', en: 'With identified successors' },
  { key: 'ready_now_coverage', es: 'Cobertura "Listo/a ahora"', en: '"Ready now" coverage' },
  { key: 'no_successor', es: 'Sin sucesor', en: 'No successor' },
  { key: 'plans_in_development', es: 'Planes en desarrollo', en: 'Plans in development' },
];

export default function AdminSuccession() {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const x = (es, en) => (isES ? es : en);
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState('');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get('/admin/clients').then((r) => {
      const list = (r.data?.items || r.data || []).filter((c) => c.status !== 'archived');
      setCompanies(list);
      if (list.length) setCid(list[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const load = useCallback(() => {
    if (!cid) return;
    setSummary(null);
    api.get(`/admin/succession/${cid}/summary`).then((r) => setSummary(r.data))
      .catch((e) => { setSummary({}); toast.error(formatApiError(e)); });
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen" data-testid="admin-succession">
      <Topbar title="Succession" subtitle={x('Supervisión de continuidad (agregada — el detalle es confidencial del cliente)', 'Continuity oversight (aggregated — detail stays confidential to the client)')} />
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={cid} onValueChange={setCid}>
            <SelectTrigger className="w-full sm:w-96 max-w-full min-w-0 [&>span]:truncate" data-testid="succ-admin-company"><SelectValue /></SelectTrigger>
            <SelectContent>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="succ-admin-confidential-note">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {x('Los roles CTH supervisan únicamente indicadores agregados. Las posiciones, titulares y sucesores son confidenciales del cliente.',
             'CTH roles supervise aggregate indicators only. Positions, incumbents and successors are confidential to the client.')}
        </p>

        {summary === null ? (
          <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            {STATS.map((s) => (
              <Card key={s.key} className="p-4 shadow-card" data-testid={`succ-admin-stat-${s.key}`}>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <UsersRound className="h-4 w-4" />
                  <span className="text-xs font-medium">{x(s.es, s.en)}</span>
                </div>
                <div className="mt-2 text-2xl font-bold tabular-nums">{summary[s.key] ?? 0}</div>
              </Card>
            ))}
          </div>
        )}
        {summary !== null && summary.critical_positions === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="succ-admin-empty">
            {x('Este cliente aún no ha registrado posiciones críticas en Succession.',
               'This client has not registered critical positions in Succession yet.')}
          </p>
        )}
      </div>
    </div>
  );
}
